import { pairHasOpenIssue, useTaskActivitiesForServiceRequest } from '@commenda/api/tasks';
import { Button, Icons } from '@commenda/kakashi';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { getServiceRequestServiceId } from '@commenda/types';
import { useRouter } from 'next/router';
import { WORK_REQUEST_CONFIG } from '../../../incorporation';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { getTaskLabel } from '../../task-labels';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { DefaultIncorporationTasks } from '../default-incorporation';

export const FillCompanyDetails = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest } = context;

  const router = useRouter();

  const reviewTask = serviceRequest?.taskGroups
    ?.find((srtg) => srtg.id === task.taskGroupId)
    ?.tasks?.find(
      (srt) => srt.legacyServiceRequestTaskTaskId === DefaultIncorporationTasks.REVIEW_CORPORATION_DETAILS
    );
  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );
  const taskHasOpenIssue = pairHasOpenIssue(
    activityFeed?.activities,
    task,
    reviewTask
  );

  const currentURL = window.location.href;

  const openWorkRequest = () => {
    if (WORK_REQUEST_CONFIG[task.workRequest.requestType]) {
      router.push({
        pathname: `${WORK_REQUEST_CONFIG[task.workRequest.requestType].urlPrefix}/${task.workRequestId}`,
        query: {
          previousURL: currentURL,
        },
      });
    } else {
      console.error('The work request type is not configured.');
    }
  };

  const getLabel = (
    role: TaskParty,
    status: TaskStatus
  ) =>
    getTaskLabel(
      getServiceRequestServiceId(serviceRequest),
      task.legacyServiceRequestTaskTaskId,
      role,
      status,
      task.title
    ) ?? task.title;

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      if (taskHasOpenIssue && task.status !== TaskStatus.COMPLETED) {
        if (state.role === TaskParty.CLIENT) {
          return {
            actions: (
              <Button size="sm" variant="secondary" onClick={openWorkRequest}>
                Fix Reported Issues
                <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Issues marked in questionnaire"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          };
        }
        if (state.role === TaskParty.AGENT) {
          return {
            actions: (
              <Button size="sm" variant="secondary" onClick={openWorkRequest}>
                Fix Issues
                <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Issues marked in questionnaire"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          };
        }
      }

      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button size="sm" onClick={openWorkRequest} variant="primary">
                Start <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.CLIENT,
                  TaskStatus.TO_DO
                )}
              />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={openWorkRequest}>
                Continue <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.CLIENT,
                  TaskStatus.IN_PROGRESS
                )}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={openWorkRequest}>
                View <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.CLIENT,
                  TaskStatus.COMPLETED
                )}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.AGENT,
                  TaskStatus.TO_DO
                )}
              />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.AGENT,
                  TaskStatus.IN_PROGRESS
                )}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(
                  TaskParty.AGENT,
                  TaskStatus.COMPLETED
                )}
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{
          role,
          status: task.status,
        }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
    </div>
  );
};
